export class ApiError extends Error {
  status: number
  code?: string
  payload: unknown

  constructor(message: string, status: number, code?: string, payload?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.payload = payload
  }
}

/** Fired on any 401 so the shell can bounce to the sign-in screen. */
export const AUTH_EXPIRED = 'wm:auth-expired'

interface Options extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** Skip the auth-expired broadcast (used by the session probe on boot). */
  quiet?: boolean
}

export async function api<T>(path: string, options: Options = {}): Promise<T> {
  const { body, quiet, headers, ...rest } = options
  const isForm = body instanceof FormData

  const res = await fetch(path, {
    credentials: 'same-origin',
    ...rest,
    headers: {
      ...(isForm || body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...headers,
    },
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
  })

  if (res.status === 204) return undefined as T

  const contentType = res.headers.get('Content-Type') ?? ''
  const data = contentType.includes('application/json')
    ? await res.json().catch(() => null)
    : await res.text().catch(() => null)

  if (!res.ok) {
    if (res.status === 401 && !quiet) {
      window.dispatchEvent(new CustomEvent(AUTH_EXPIRED))
    }
    const detail = data as { error?: string; code?: string } | null
    throw new ApiError(
      detail?.error ?? `Request failed (${res.status})`,
      res.status,
      detail?.code,
      data,
    )
  }

  return data as T
}

export const get = <T>(path: string) => api<T>(path)
export const post = <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body })
export const patch = <T>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body })
export const put = <T>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body })
export const del = <T>(path: string) => api<T>(path, { method: 'DELETE' })

/** True when the failure looks like "no network" rather than "server said no". */
export function isOffline(err: unknown): boolean {
  return !navigator.onLine || (err instanceof TypeError && /fetch/i.test(err.message))
}
